import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { adminAPI, locationsAPI } from '../../../api/bookings';

export function useCustomerManagement() {
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [locations, setLocations] = useState({});
  // Staff name lookup — BookingCard uses this to resolve
  // created_by_staff / cancelled_by_id / marked_paid_by_staff into
  // real display names (e.g. "Cancelled by Andy Lau"). Fetched once
  // when the page mounts; small dataset (<50 rows).
  const [staffMap, setStaffMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  // Activation status filter — separate from typeFilter because status
  // and type are orthogonal concepts (a guest doesn't carry an
  // account_status at all, so the status filter naturally narrows the
  // type filter when "active"/"inactive" is selected).
  const [statusFilter, setStatusFilter] = useState('all');

  // Customer detail view
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [detailsLoading, setDetailsLoading] = useState(false);

  // Activation-toggle dialog state. Single dialog instance, parameterised
  // by `pendingAction` so we re-use copy + Tailwind variants. Closing
  // the dialog clears the action so re-opening starts fresh.
  const [pendingAction, setPendingAction] = useState(null); // 'deactivate' | 'activate' | null
  const [toggling, setToggling] = useState(false);

  // Guest email edit dialog — only opens for `type === 'guest'`.
  // Registered users are hard-rejected server-side (403) even if the
  // UI is bypassed. Name stays fixed in v1 (per user decision).
  const [emailEditOpen, setEmailEditOpen] = useState(false);
  const [emailDraft, setEmailDraft] = useState('');
  const [savingEmail, setSavingEmail] = useState(false);

  const loadLocations = useCallback(async () => {
    try {
      const locs = await locationsAPI.getAllLocations();
      const locsMap = {};
      locs.forEach(loc => { locsMap[loc.id] = loc; });
      setLocations(locsMap);
    } catch (error) {
      console.error('Failed to load locations:', error);
    }
  }, []);

  // One-shot staff fetch — same fallback pattern as BookingsManagement.
  // The hardcoded id is a legacy pre-RBAC admin account that predates
  // db.staff seeding; kept so old audit rows still resolve to a name.
  const loadStaff = useCallback(async () => {
    const fallback = { 'admin-hardcoded-001': 'Rong Zhang' };
    try {
      const list = await adminAPI.getStaff();
      const map = { ...fallback };
      (list || []).forEach((s) => {
        if (s.id) map[s.id] = s.name || s.email || s.id.slice(0, 8);
      });
      setStaffMap(map);
    } catch {
      setStaffMap(fallback);
    }
  }, []);

  const loadCustomers = useCallback(async () => {
    setLoading(true);
    try {
      const data = await adminAPI.getCustomers();
      setCustomers(data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadCustomers();
    loadLocations();
    loadStaff();
  }, [loadCustomers, loadLocations, loadStaff]);

  const filterCustomers = useCallback(() => {
    let filtered = [...customers];

    // Filter by search term
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(c =>
        c.name?.toLowerCase().includes(search) ||
        c.phone?.toLowerCase().includes(search) ||
        c.email?.toLowerCase().includes(search)
      );
    }

    // Filter by type
    if (typeFilter !== 'all') {
      filtered = filtered.filter(c => c.type === typeFilter);
    }

    // Filter by activation status — only meaningful for registered users.
    // Selecting "active" or "inactive" implicitly hides guests (they
    // have no account_status field). Selecting "all" is a no-op.
    if (statusFilter !== 'all') {
      filtered = filtered.filter(c => (c.account_status || 'active') === statusFilter);
    }

    setFilteredCustomers(filtered);
  }, [customers, searchTerm, typeFilter, statusFilter]);

  useEffect(() => {
    filterCustomers();
  }, [filterCustomers]);

  const viewCustomerDetails = useCallback(async (customer) => {
    setSelectedCustomer(customer);
    setDetailsLoading(true);
    try {
      const details = await adminAPI.getCustomerDetails(customer.id);
      setCustomerDetails(details);
    } catch (error) {
      toast.error('Failed to load customer details');
    } finally {
      setDetailsLoading(false);
    }
  }, []);

  const closeDetails = useCallback(() => {
    setSelectedCustomer(null);
    setCustomerDetails(null);
  }, []);

  // Activation toggle — fired from the AlertDialog "confirm" action.
  // Optimistic UX: we patch immediately, then re-fetch the detail to
  // pick up the authoritative state (status_changed_at, etc.) and
  // also refresh the list so the per-card dot + status filter reflect
  // the change after the admin clicks Back.
  const handleConfirmToggle = useCallback(async () => {
    if (!pendingAction || !customerDetails) return;
    const newStatus = pendingAction === 'deactivate' ? 'inactive' : 'active';
    const customerId = customerDetails.customer.id;
    const customerName = customerDetails.customer.name || 'customer';

    setToggling(true);
    try {
      await adminAPI.updateCustomerStatus(customerId, newStatus);
      // Re-fetch detail (authoritative server state)
      const fresh = await adminAPI.getCustomerDetails(customerId);
      setCustomerDetails(fresh);
      // Refresh list quietly so the dot + filter are up to date when the
      // admin returns. Failure here is non-fatal — they can Back+forward.
      loadCustomers().catch(() => {});

      toast.success(
        newStatus === 'inactive'
          ? `${customerName} has been deactivated`
          : `${customerName} has been reactivated`,
      );
      setPendingAction(null);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update customer status');
    } finally {
      setToggling(false);
    }
  }, [pendingAction, customerDetails, loadCustomers]);

  // Open the email edit dialog seeded with the current email so staff
  // can either correct a typo or just replace it wholesale.
  const openEmailEdit = useCallback(() => {
    if (!customerDetails || customerDetails.customer.type !== 'guest') return;
    setEmailDraft(customerDetails.customer.email || '');
    setEmailEditOpen(true);
  }, [customerDetails]);

  const handleSaveEmail = useCallback(async () => {
    if (!customerDetails) return;
    const nextEmail = emailDraft.trim();
    if (!nextEmail) {
      toast.error('Email cannot be empty');
      return;
    }
    // Lightweight client-side check — server does the authoritative
    // validation. Keeps UX snappy for the common typo case.
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(nextEmail)) {
      toast.error('Please enter a valid email address');
      return;
    }

    setSavingEmail(true);
    try {
      const fresh = await adminAPI.updateGuestProfile(
        customerDetails.customer.id,
        { email: nextEmail },
      );
      setCustomerDetails(fresh);
      // Keep the list view in sync so email shows up on Back.
      loadCustomers().catch(() => {});
      toast.success('Guest email updated');
      setEmailEditOpen(false);
    } catch (error) {
      toast.error(error?.response?.data?.detail || 'Failed to update email');
    } finally {
      setSavingEmail(false);
    }
  }, [customerDetails, emailDraft, loadCustomers]);

  return {
    customers, filteredCustomers, locations, staffMap, loading,
    searchTerm, setSearchTerm, typeFilter, setTypeFilter, statusFilter, setStatusFilter,
    selectedCustomer, customerDetails, detailsLoading,
    pendingAction, setPendingAction, toggling,
    emailEditOpen, setEmailEditOpen, emailDraft, setEmailDraft, savingEmail,
    viewCustomerDetails, closeDetails, handleConfirmToggle, openEmailEdit, handleSaveEmail,
  };
}
